import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicInvoice, createCheckoutSession, verifyPayment } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { StatusDot } from '@/components/ui/status-dot';
import { DocumentMark } from '@/components/ui/document-mark';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Loader2, Download } from 'lucide-react';

const formatCurrency = (a, cur) => new Intl.NumberFormat(cur === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency: cur || 'USD' }).format(a || 0);

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PublicInvoice() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchInvoice = useCallback(() => {
    getPublicInvoice(token).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { alert('Failed to load payment gateway. Please try again.'); setPaying(false); return; }

      const res = await createCheckoutSession(token);
      const { order_id, key_id, amount, currency, name } = res.data;

      const options = {
        key: key_id,
        amount,
        currency,
        name: 'Flowlancer',
        description: name,
        order_id,
        prefill: { email: data?.client?.email || '' },
        theme: { color: '#1F4D3A' },
        handler: async (response) => {
          setVerifying(true);
          setPaying(false);
          try {
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            fetchInvoice();
          } catch {
            alert('Payment verification failed. Please contact support.');
          } finally {
            setVerifying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch {
      setPaying(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-paper flex items-center justify-center"><p className="text-small text-ink-muted">Loading invoice…</p></div>;
  if (!data) return <div className="min-h-screen bg-paper flex items-center justify-center"><p className="text-small text-ink-muted">Invoice not found</p></div>;

  const { invoice, freelancer, client } = data;
  const isPaid = invoice.status === 'paid';

  return (
    <div className="min-h-screen bg-paper px-6 py-12 md:py-20" data-testid="public-invoice-page">
      <div className="mx-auto max-w-[720px]">
        <DocumentMark freelancer={freelancer} />

        <div className="mt-10" data-testid="invoice-card">
          {/* Header */}
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="text-micro uppercase text-ink-subtle">From</p>
              <p className="mt-1.5 text-body text-ink">{freelancer.business_name || freelancer.name}</p>
              {freelancer.address && <p className="whitespace-pre-line text-small text-ink-muted">{freelancer.address}</p>}
              <p className="text-small text-ink-muted">{freelancer.email}</p>
            </div>
            <div className="text-right">
              <h1 className="font-display text-page-title text-ink">Invoice</h1>
              <p className="font-mono text-small text-ink-muted">#{invoice.invoice_number}</p>
            </div>
          </div>

          {/* Client & dates */}
          <div className="mt-8 flex items-start justify-between gap-6 border-t border-line pt-6">
            <div>
              <p className="text-micro uppercase text-ink-subtle">Bill to</p>
              <p className="mt-1.5 text-body text-ink">{client.name}</p>
              {client.company && <p className="text-small text-ink-muted">{client.company}</p>}
              {client.address && <p className="whitespace-pre-line text-small text-ink-muted">{client.address}</p>}
            </div>
            <div className="text-right">
              <p className="text-small text-ink-muted tabular-nums">
                Issued {new Date(invoice.created_at).toLocaleDateString()}
              </p>
              {invoice.due_date && (
                <p className="text-small text-ink-muted tabular-nums">
                  Due {new Date(invoice.due_date).toLocaleDateString()}
                </p>
              )}
              <div className="mt-2 flex justify-end">
                <StatusDot status={invoice.status} />
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="mt-10">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(invoice.line_items || []).map((item, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-ink">{item.description}</TableCell>
                    <TableCell className="text-right text-ink-muted">{item.qty}</TableCell>
                    <TableCell className="text-right text-ink-muted">{formatCurrency(item.rate, invoice.currency)}</TableCell>
                    <TableCell className="text-right text-ink">{formatCurrency(item.amount, invoice.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div className="mt-8 flex justify-end">
            <div className="w-full max-w-[280px] space-y-1.5">
              <div className="flex justify-between text-small">
                <span className="text-ink-muted">Subtotal</span>
                <span className="text-ink tabular-nums">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
              </div>
              {invoice.tax_percent > 0 && (
                <div className="flex justify-between text-small">
                  <span className="text-ink-muted">Tax ({invoice.tax_percent}%)</span>
                  <span className="text-ink tabular-nums">{formatCurrency(invoice.tax_amount, invoice.currency)}</span>
                </div>
              )}
              <div className="flex items-baseline justify-between border-t border-line-strong pt-3">
                <span className="text-micro uppercase text-ink-subtle">Total</span>
                <span className="font-sans text-metric tabular-nums text-ink">
                  {formatCurrency(invoice.total, invoice.currency)}
                </span>
              </div>
            </div>
          </div>

          {/* Pay */}
          {!isPaid && (
            <div className="mt-10 border-t border-line pt-8">
              {verifying ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-ink-subtle" strokeWidth={1.5} />
                  <p className="text-small text-ink-muted">Verifying payment…</p>
                </div>
              ) : (
                <Button onClick={handlePay} disabled={paying} size="lg" className="w-full" data-testid="pay-now-btn">
                  {paying ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> Opening payment…</>
                  ) : (
                    <>Pay {formatCurrency(invoice.total, invoice.currency)}</>
                  )}
                </Button>
              )}
            </div>
          )}

          {isPaid && (
            <div className="mt-10 border-t border-line pt-8">
              <p className="font-display text-subhead text-ink">Payment received</p>
              <p className="mt-1 text-small text-ink-muted">Thank you. This invoice is settled.</p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                {invoice.project_id && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={`/delivery/${token}`}>Download deliverables</a>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    import('@/lib/pdfExport').then(({ generateInvoicePDF }) => {
                      generateInvoicePDF(invoice, freelancer, client);
                    });
                  }}
                  data-testid="export-pdf-btn"
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
